import { useEffect, useState } from "react";
import api from "../../utils/api";
import toast from "react-hot-toast";

export default function AssignPermissions() {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchRoles();
    fetchModules();
  }, []);

  useEffect(() => {
    if (selectedRole) {
      fetchRolePermissions(selectedRole);
    } else {
      setSelectedPermissions([]);
    }
  }, [selectedRole]);

  const fetchRoles = async () => {
    try {
      const res = await api.get("/admin/roles");
      setRoles(res.data);
    } catch {
      toast.error("Failed to load roles");
    }
  };

  const fetchModules = async () => {
    try {
      const res = await api.get("/admin/modules");
      setModules(res.data.data);
    } catch {
      toast.error("Failed to load modules");
    }
  };

  const fetchRolePermissions = async (roleId) => {
    try {
      const res = await api.get(`/admin/role/${roleId}/permissions`);

      setSelectedPermissions(
        (res.data.permissions || []).map((perm) => perm.name)
      );
    } catch {
      toast.error("Failed to load permissions");
    }
  };

  const togglePermission = (perm) => {
    setSelectedPermissions((prev) =>
      prev.includes(perm)
        ? prev.filter((p) => p !== perm)
        : [...prev, perm]
    );
  };

  const toggleModulePermissions = (moduleName) => {
    const modulePerms = ["view", "create", "edit", "delete"].map(
      (action) => `${moduleName}.${action}`
    );

    const allSelected = modulePerms.every((perm) =>
      selectedPermissions.includes(perm)
    );

    if (allSelected) {
      setSelectedPermissions((prev) =>
        prev.filter((perm) => !modulePerms.includes(perm))
      );
    } else {
      setSelectedPermissions((prev) => [
        ...new Set([...prev, ...modulePerms]),
      ]);
    }
  };

  const handleSubmit = async () => {
    if (!selectedRole) {
      toast.error("Select role");
      return;
    }

    try {
      setLoading(true);

      await api.post(`/admin/roles/${selectedRole}/permissions`, {
        permissions: selectedPermissions,
      });

      toast.success("Permissions updated");
    } catch {
      toast.error("Failed to assign permissions");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-800">
          Assign Permissions
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Select role and manage permissions module-wise
        </p>
      </div>

      {/* Role Select */}
      <div className="mb-8">
        <label className="block text-sm font-medium text-slate-700 mb-2">
          Select Role
        </label>

        <select
          className="w-full border border-slate-300 rounded-xl p-3 focus:ring-2 focus:ring-blue-500 outline-none"
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value)}
        >
          <option value="">Select Role</option>

          {roles.map((role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </select>
      </div>

      {/* Permissions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {modules.map((module) => {
          const allSelected = ["view", "create", "edit", "delete"].every(
            (action) =>
              selectedPermissions.includes(`${module.name}.${action}`)
          );

          return (
            <div
              key={module.id}
              className="border border-slate-200 rounded-2xl p-5 shadow-sm"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800 capitalize">
                  {module.name}
                </h3>

                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() =>
                      toggleModulePermissions(module.name)
                    }
                    className="w-4 h-4"
                  />
                  Select All
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {["view", "create", "edit", "delete"].map((action) => {
                  const permission = `${module.name}.${action}`;

                  return (
                    <label
                      key={permission}
                      className={`flex items-center gap-3 border rounded-xl p-3 cursor-pointer transition
                      ${selectedPermissions.includes(permission)
                          ? "bg-blue-50 border-blue-400"
                          : "border-slate-200 hover:border-slate-300"
                        }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedPermissions.includes(permission)}
                        onChange={() => togglePermission(permission)}
                        className="w-4 h-4"
                      />

                      <span className="text-sm font-medium text-slate-700 capitalize">
                        {action}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Submit */}
      <div className="mt-8 flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={loading}
          className={`px-6 py-3 rounded-xl text-white font-medium transition
          ${loading
              ? "bg-slate-400 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
            }`}
        >
          {loading ? "Saving..." : "Assign Permissions"}
        </button>
      </div>
    </div>
  );
}